import { useCallback, useMemo } from "react";
import type { ChangeEvent } from "react";
import { Checkbox, Select, Stack } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import type {
  LegacyReportDraftData,
  LegacyReportReviewAsset,
} from "../../../types/legacyReportReview";
import type { LegacyHistoricalReportAsset } from "../../../types/legacyHistoricalReport";

interface Props {
  asset: LegacyReportReviewAsset;
  form: UseFormReturnType<LegacyReportDraftData>;
  disabled: boolean;
}

export default function LegacyReportAssetPlacement({
  asset,
  form,
  disabled,
}: Props) {
  const verified = useMemo<LegacyHistoricalReportAsset>(
    () => ({
      id: asset.id,
      sha256: asset.sha256,
      mimeType: asset.mimeType,
      width: asset.width,
      height: asset.height,
      sourcePage: asset.sourcePage,
    }),
    [asset]
  );
  const organMap = useMemo(
    () => form.values.organMaps.find((item) => item.id === asset.id),
    [form.values.organMaps, asset.id]
  );
  const signatureId = useMemo(
    () =>
      form.values.signatures.find(
        (signature) => signature.asset?.id === asset.id
      )?.id || null,
    [form.values.signatures, asset.id]
  );
  const signatures = useMemo(
    () =>
      form.values.signatures
        .map((signature, index) => ({
          value: signature.id,
          label: `Attribution ${index + 1} · source page ${
            signature.sourcePage || "unknown"
          }`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [form.values.signatures]
  );
  const sections = useMemo(
    () =>
      form.values.sections.map((section, index) => ({
        value: section.id,
        label: `${index + 1}. ${section.label || "Historical section"}`,
      })),
    [form.values.sections]
  );
  const handleOrganMap = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const current = form
        .getValues()
        .organMaps.filter((item) => item.id !== asset.id);
      form.setFieldValue(
        "organMaps",
        event.currentTarget.checked ? [...current, verified] : current
      );
    },
    [form, asset.id, verified]
  );
  const handleSignature = useCallback(
    (value: string | null) => {
      form.setFieldValue(
        "signatures",
        form.getValues().signatures.map((signature) => {
          if (signature.id === value) return { ...signature, asset: verified };
          if (signature.asset?.id !== asset.id) return signature;
          const { asset: removed, ...remaining } = signature;
          return remaining;
        })
      );
    },
    [form, asset.id, verified]
  );
  const handlePosition = useCallback(
    (value: string | null) => {
      form.setFieldValue(
        "organMaps",
        form.getValues().organMaps.map((item) => {
          if (item.id !== asset.id) return item;
          const { afterSectionId: previous, ...remaining } = item;
          return value ? { ...remaining, afterSectionId: value } : remaining;
        })
      );
    },
    [form, asset.id]
  );
  return (
    <Stack gap="sm">
      <Checkbox
        label="Preserve this graphic as an organ map with its original labels and legend"
        checked={Boolean(organMap)}
        onChange={handleOrganMap}
        disabled={disabled}
      />
      {organMap && (
        <Select
          label="Place organ map after historical section"
          placeholder="After historical text"
          data={sections}
          value={organMap.afterSectionId || null}
          onChange={handlePosition}
          clearable
          disabled={disabled}
        />
      )}
      {signatures.length > 0 && (
        <Select
          label="Use graphic for historical attribution"
          placeholder="No signature graphic selected"
          data={signatures}
          value={signatureId}
          onChange={handleSignature}
          clearable
          disabled={disabled}
        />
      )}
    </Stack>
  );
}
